import { Trans } from '@lingui/macro';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import Link from '../../links/Link';
import ConfirmDataTableActionButton from '../../form/ConfirmDataTableActionButton';
import { COPY, CREATE, DELETE, DISABLE, ENABLE, SCHEDULE } from '../../../services/FormDataTableActionService';
import { LIST_NEED_ALERT } from '../infrastructure/InfrastructureConstants';
import { getTestTypeDisplayTitle } from './ServiceTestsConstants';
import { useNavigate } from '../../../utilities/PgmUtilities';
import { buildCopyNavigationPath } from './DuplicateTestUtils';
import { MenusItemsArrayButton } from '../infrastructure/MenusItemsArrayButton';

const CHANGE_RUN_SCHEDULE_LABEL = 'Change Run Schedule';

export function ServiceTestsListActions({
    isDataTableActionButton,
    dataTableActioning,
    tableSelection,
    tableState,
    handleConfirmedDataTableAction,
    onScheduleClick,
    filterState,
    selectionData,
    testTypes,
    testTypeArrayObject,
}) {
    const navigate = useNavigate();
    const selectionDataCurrent = selectionData?.current;

    const testsTableSelection = useMemo(() => {
        return {
            ...tableSelection,
            ...selectionDataCurrent,
            testTypes,
        };
    }, [selectionDataCurrent, tableSelection, testTypes]);

    const scheduleHandlerCallback = useCallback(
        (actionPayloadParams) => {
            return handleConfirmedDataTableAction(
                null,
                SCHEDULE,
                actionPayloadParams,
                filterState,
                testsTableSelection,
                tableState,
                LIST_NEED_ALERT
            );
        },
        [filterState, handleConfirmedDataTableAction, tableState, testsTableSelection]
    );

    const isCopyAction = isDataTableActionButton || selectionDataCurrent?.selectionLength !== 1;

    const handleScheduleAction = (event) => {
        event.currentTarget.blur();
        event.preventDefault();
        event.stopPropagation();

        onScheduleClick(scheduleHandlerCallback);
    };

    const handleCopyAction = (event) => {
        event.currentTarget.blur();
        event.preventDefault();
        event.stopPropagation();

        const selectedId = selectionDataCurrent?.currentTableSelection?.selection?.[0];
        if (selectedId) {
            navigate(buildCopyNavigationPath(selectedId));
        }
    };

    return (
        <>
            <ConfirmDataTableActionButton
                key={SCHEDULE}
                label={CHANGE_RUN_SCHEDULE_LABEL}
                disabled={isDataTableActionButton}
                handleConfirmedDataTableAction={handleScheduleAction}
                needConfirm={false}
            />
            <ConfirmDataTableActionButton
                key={DELETE}
                label={DELETE}
                disabled={isDataTableActionButton}
                plural={tableSelection?.selection?.length > 1}
                handleConfirmedDataTableAction={(event) =>
                    handleConfirmedDataTableAction(
                        event,
                        DELETE,
                        null,
                        filterState,
                        testsTableSelection,
                        tableState,
                        LIST_NEED_ALERT
                    )
                }
                needConfirm
            />
            <ConfirmDataTableActionButton
                key={ENABLE}
                label={ENABLE}
                disabled={isDataTableActionButton}
                handleConfirmedDataTableAction={(event) =>
                    handleConfirmedDataTableAction(
                        event,
                        ENABLE,
                        null,
                        filterState,
                        testsTableSelection,
                        tableState,
                        LIST_NEED_ALERT
                    )
                }
                needConfirm={false}
            />
            <ConfirmDataTableActionButton
                key={DISABLE}
                label={DISABLE}
                disabled={isDataTableActionButton}
                handleConfirmedDataTableAction={(event) =>
                    handleConfirmedDataTableAction(
                        event,
                        DISABLE,
                        null,
                        filterState,
                        testsTableSelection,
                        tableState,
                        LIST_NEED_ALERT
                    )
                }
                needConfirm={false}
            />
            <ConfirmDataTableActionButton
                key={COPY}
                label={COPY}
                disabled={isCopyAction}
                handleConfirmedDataTableAction={handleCopyAction}
                needConfirm={false}
            />
            {testTypeArrayObject.length === 1 && (
                <Button
                    key={CREATE}
                    component={Link}
                    disabled={dataTableActioning}
                    to="new"
                    variant="contained"
                    color="primary"
                >
                    <Trans>Create</Trans>
                </Button>
            )}
            {testTypeArrayObject.length > 1 && (
                <MenusItemsArrayButton key={CREATE} label={<Trans>Create</Trans>} disabled={dataTableActioning}>
                    {(closeMenu) =>
                        testTypeArrayObject.map((type) => (
                            <MenuItem
                                key={type.name}
                                onClick={() => {
                                    closeMenu();
                                    navigate(`new?name=${encodeURIComponent(type.name)}`);
                                }}
                            >
                                {getTestTypeDisplayTitle(type)}
                            </MenuItem>
                        ))
                    }
                </MenusItemsArrayButton>
            )}
        </>
    );
}

ServiceTestsListActions.propTypes = {
    isDataTableActionButton: PropTypes.bool.isRequired,
    dataTableActioning: PropTypes.bool.isRequired,
    tableSelection: PropTypes.shape({
        selection: PropTypes.array.isRequired,
        inverted: PropTypes.bool.isRequired,
    }),
    tableState: PropTypes.object,
    handleConfirmedDataTableAction: PropTypes.func.isRequired,
    onScheduleClick: PropTypes.func,
    filterState: PropTypes.object,
    selectionData: PropTypes.shape({
        current: PropTypes.object,
    }),
    testTypes: PropTypes.array,
    testTypeArrayObject: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            _id: PropTypes.string,
        })
    ),
};

ServiceTestsListActions.defaultProps = {
    tableSelection: { selection: [], inverted: false },
    tableState: {},
    onScheduleClick: undefined,
    filterState: {},
    selectionData: null,
    testTypes: [],
    testTypeArrayObject: [],
};
